import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/auth/AuthContext";
import { isFeatureEnabled, getUserFeatures } from "@/services/features.service";
import logger from "@/utils/logger";

export interface FeatureCache {
  [key: string]: boolean;
}

// Feature considerate "premium": un utente premium le ha tutte. Serve per
// sbloccarle subito dopo l'acquisto, anche se il featureCache (caricato da
// user_features) è ancora stale finché il webhook non aggiorna il server.
const PREMIUM_FEATURE_KEYS = new Set([
  'unlimited_lists',
  'list_backgrounds',
  'product_photos',
  'custom_themes',
  'offline_mode',
  'list_store_binding',
  'purchase_history',
]);

const isPremiumFeatureKey = (key: string): boolean =>
  key.startsWith('premium_') || PREMIUM_FEATURE_KEYS.has(key);

const useFeature = () => {
  const { user } = useAuth();
  const [featureCache, setFeatureCache] = useState<FeatureCache>({});
  const [loadingFeatures, setLoadingFeatures] = useState<boolean>(true);
  const inFlightChecks = useRef(new Map<string, Promise<boolean>>());

  // Carica tutte le feature dell'utente una volta sola
  useEffect(() => {
    if (user?.id) {
      setLoadingFeatures(true);
      getUserFeatures()
        .then(userFeatures => {
          const newCache: FeatureCache = {};
          userFeatures.forEach(item => {
            newCache[item.key] = item.feature.isEnabled;
          });
          setFeatureCache(newCache);
        })
        .catch(err => {
          console.error("Failed to load user features:", err);
        })
        .finally(() => {
          setLoadingFeatures(false);
        });
    } else {
      setFeatureCache({});
      setLoadingFeatures(false);
    }
    // Ricarica anche al cambio di isPremium: dopo l'acquisto il server (via
    // webhook) concede le feature premium → il cache va rifetchato.
  }, [user?.id, user?.isPremium]);

  // Accesso asincrono: carica feature se non disponibile
  const checkFeatureEnabled = useCallback((featureKey: string): Promise<boolean> => {
    if (featureCache[featureKey] !== undefined) {
      return Promise.resolve(featureCache[featureKey]);
    }

    if (inFlightChecks.current.has(featureKey)) {
      return inFlightChecks.current.get(featureKey)!;
    }

    const promise = isFeatureEnabled(featureKey)
      .then(enabled => {
        setFeatureCache(prev => ({ ...prev, [featureKey]: enabled }));
        return enabled;
      })
      .catch(error => {
        logger.logError("useFeature",`Error checking feature `,{additionalInfo:error});
        setFeatureCache(prev => ({ ...prev, [featureKey]: false }));
        return false;
      })
      .finally(() => {
        inFlightChecks.current.delete(featureKey);
      });

    inFlightChecks.current.set(featureKey, promise);
    return promise;
  }, [featureCache]);

  // Accesso sincrono: solo dalla cache o fallback
  const isFeatureEnabledSync = useCallback((featureKey: string): boolean => {
    // Un utente premium ha SEMPRE tutte le feature premium: questo le sblocca
    // immediatamente dopo l'acquisto (quando isPremium diventa true in modo
    // ottimistico), senza attendere il rifetch del featureCache.
    if (user?.isPremium && isPremiumFeatureKey(featureKey)) {
      return true;
    }
    if (featureCache[featureKey] !== undefined) {
      return featureCache[featureKey];
    }

    const defaultFeatureState: Record<string, boolean> = {
      'create_one_list': true,
      'login_two_devices': true,
      'quick_add_products': true,
      'recurring_products': true,
      'quick_insert': true,
      'unlimited_categories': true,
      'hide_categories': true,
      'unlimited_products': true,
      // Lo storico acquisti è ora una funzionalità premium
      'purchase_history': user?.isPremium || false,
      'light_dark_theme': true,
      'list_sharing': true,
      'unlimited_lists': user?.isPremium || false,
      'custom_themes': user?.isPremium || false,
      'list_store_binding': user?.isPremium || false
    };

    return defaultFeatureState[featureKey] ?? false;
  }, [featureCache, user?.isPremium]);

  return {
    isFeatureEnabled: isFeatureEnabledSync,
    checkFeatureEnabled,
    loadingFeatures,
    featureCache
  };
};

export default useFeature;
